import React, { useState } from "react";
import axios from "axios";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import BookingModal from "../components/BookingModal";
import { company } from "../data/mock";
import { Phone, Mail, MapPin, Clock, CalendarDays } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { SITE_URL } from "../lib/site";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast({ title: "Message sent!", description: "We'll get back to you within 24 hours." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Call or text", value: company.phone, href: company.phoneLink },
    { icon: Mail, label: "Email us", value: company.email, href: `mailto:${company.email}` },
    { icon: MapPin, label: "Location", value: company.location },
    { icon: Clock, label: "Hours", value: "Mon–Sat: 8am – 7pm" },
  ];

  return (
    <main>
      <SEO
        title="Contact Us"
        description="Contact Set Up Home Theater for commercial & residential AV installation in Rocky Mount, NC. Call (252) 545-0011 or send us a message for a free quote."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Contact Us", item: `${SITE_URL}/contact` },
          ],
        }}
      />
      <PageHeader
        breadcrumb="Home / Contact Us"
        title="Get in touch with our team"
        subtitle="Have a question or ready to book? Reach out and we'll respond fast."
        note="Chris is a one-man shop and only books a limited number of installs each week — reserving ahead secures your slot."
      />
      <div className="bg-[#0a1f44]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-12 -mt-6">
          <button onClick={() => setBookingOpen(true)} data-testid="book-consult-btn" className="inline-flex items-center gap-2 bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-semibold px-6 py-3.5 rounded-full transition-colors shadow-lg shadow-[#3b9dff]/20">
            <CalendarDays className="w-4 h-4" /> Book a Consultation
          </button>
        </div>
      </div>
      <section className="section-pad">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <span className="eyebrow">Contact information</span>
            <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-8">
              We'd love to hear from you
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {info.map((i) => {
                const Icon = i.icon;
                const content = (
                  <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow h-full">
                    <span className="inline-flex w-11 h-11 rounded-xl bg-[#eef4ff] items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[#3b9dff]" />
                    </span>
                    <p className="text-slate-400 text-sm">{i.label}</p>
                    <p className="text-[#0a1f44] font-semibold mt-1">{i.value}</p>
                  </div>
                );
                return i.href ? (
                  <a key={i.label} href={i.href}>{content}</a>
                ) : (
                  <div key={i.label}>{content}</div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm">
            <h3 className="text-xl font-bold text-[#0a1f44] mb-6">Send us a message</h3>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="cname">Full name</Label>
                <Input id="cname" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5" placeholder="John Doe" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cemail">Email</Label>
                  <Input id="cemail" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5" placeholder="you@email.com" />
                </div>
                <div>
                  <Label htmlFor="cphone">Phone</Label>
                  <Input id="cphone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5" placeholder="(252) 000-0000" />
                </div>
              </div>
              <div>
                <Label htmlFor="cmsg">Message</Label>
                <Textarea id="cmsg" required value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5" rows={4} placeholder="How can we help?" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors disabled:opacity-60">
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </main>
  );
};

export default Contact;
